import { useTheme } from '@emotion/react';
import { Box, Card, CardContent, Typography } from '@mui/material';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useEffect, useRef } from 'react';
import { darkTheme, lightTheme } from '../../../components/highcharts/themes';
import InfoTooltipIcon from '@/components/icons/InfoTooltipIcon';
import { formatNumber } from '@/utils/formatters';
const PortfolioAnalysisBenchmarkChart = ({
  address = 'Adresse 1',
  data = {},
  isLoading,
}) => {
  const chartComponentRef = useRef(null);
  const theme = useTheme();

  const kdePoints = data.BenchmarkPDF?.map((d) => [d.Value, d.Density]) || [];
  const benchmarkX = data.BenchmarkData?.kWhPrYPrM2 ?? 0;
  const benchmarkY = kdePoints.find(([x]) => x >= benchmarkX)?.[1] || 0;
  const benchmarkPoint = {
    x: benchmarkX,
    y: benchmarkY,
    cdf: data.BenchmarkData?.BenchmarkCDF ?? 0,
    relative: data.BenchmarkData?.MedianDiff ?? 0,
  };
  const medianValue = data.BenchmarkData?.GroupMedian ?? 0;

  const options = {
    chart: {
      type: 'line',
      zoomType: 'x',
      backgroundColor: 'transparent',
    },
    title: { text: '' },
    xAxis: {
      title: { text: 'kWh pr. år pr. m²' },
      plotLines: [
        {
          color: 'black',
          dashStyle: 'Dash',
          width: 2,
          value: medianValue,
          zIndex: 5,
        },
      ],
      labels: {
        style: {
          color: theme.palette.mode === 'dark' ? 'white' : 'black',
        },
      },
    },
    yAxis: {
      title: { text: 'Tæthed' },
      labels: {
        enabled: false,
        style: {
          color: theme.palette.mode === 'dark' ? 'white' : 'black',
        },
      },
    },
    tooltip: {
      useHTML: true,
      formatter: function () {
        if (this.series.name === 'Benchmarking Point') {
          return `
                        <b>${'Din bygning'}</b><br/>
                    `;
        }
        return false;
      },
    },
    series: [
      {
        name: 'Sandsynlighedstæthed',
        data: kdePoints,
        color: 'hsl(63, 100%, 40%)',
        lineWidth: 2,
        marker: { enabled: false },
        type: 'area',
        fillOpacity: 0.3,
        fillColor: {
          linearGradient: [0, 0, 0, 300],
          stops: [
            [0, 'hsl(63, 100%, 40%, 0.7)'],
            [1, 'hsl(63, 100%, 40%, 0.1)'],
          ],
        },
        showInLegend: true,
        enableMouseTracking: false,
      },
      {
        name: `Benchmarking Point`,
        type: 'scatter',
        data: [benchmarkPoint],
        color: 'hsl(63, 100.00%, 25%)',
        marker: {
          radius: 5,
          symbol: 'circle',
        },
        showInLegend: false,
        enableMouseTracking: true,
        stickyTracking: false,
      },
      {
        name: `Median - ${formatNumber(medianValue)}`,
        type: 'line',
        data: [],
        color: 'black',
        dashStyle: 'Dash',
        marker: { enabled: false },
        enableMouseTracking: false,
        showInLegend: true,
      },
    ],
    legend: {
      enabled: true,
      itemStyle: {
        color: theme.palette.mode === 'dark' ? 'white' : 'black',
      },
    },
  };

  useEffect(() => {
    chartComponentRef.current?.chart?.reflow();
  }, [theme]);
  useEffect(() => {
    const themeOptions = theme.palette.mode === 'dark' ? darkTheme : lightTheme;

    if (chartComponentRef.current?.chart) {
      const chart = chartComponentRef.current.chart;
      chart.update(
        {
          chart: { backgroundColor: themeOptions.chart.backgroundColor },
          time: {
            useUTC: true,
          },
          title: { style: { color: themeOptions.title.style.color } },
          xAxis: {
            gridLineColor: 'transparent',
            lineColor: themeOptions.xAxis.lineColor,
            labels: { style: { color: themeOptions.xAxis.labels.style.color } },
          },
          yAxis: [
            {
              gridLineColor: 'transparent',
              labels: {
                style: { color: themeOptions.yAxis.labels.style.color },
              },
            },
          ],
          legend: {
            itemStyle: { color: themeOptions.legend.itemStyle.color },
          },
          tooltip: {
            backgroundColor: themeOptions.tooltip.backgroundColor,
            borderColor: themeOptions.tooltip.borderColor,
            style: {
              color: theme.palette.mode === 'dark' ? 'white' : 'black',
            },
          },
        },
        false,
      );
      chart.redraw();
    }
  }, [theme]);
  useEffect(() => {
    const chart = chartComponentRef.current?.chart;
    if (chart) {
      if (isLoading) {
        chart.showLoading('Loading data...');
      } else {
        chart.hideLoading();
      }
    }
  }, [isLoading]);
  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="body1" color="textSecondary" fontWeight="bold">
            {address}
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          my: 2,
          display: 'flex',
          justifyContent: 'space-around',
          gap: 2,
          fontSize: '1rem',
          fontFamily: 'sans-serif',
          textAlign: 'center',
        }}
      >
        <Card
          variant="outlined"
          sx={{ paddingX: '4rem', height: '100%', borderRadius: 2 }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography
                  variant="body1"
                  color="textSecondary"
                  fontWeight="bold"
                >
                  Forbrug pr m² pr år
                </Typography>
                <InfoTooltipIcon title="Her vises forbruget pr kvadratmeter." />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Typography variant="h6" fontWeight="bold">
                {formatNumber(benchmarkPoint.x)} kWh/m²
              </Typography>
            </Box>
          </CardContent>
        </Card>
        <Card
          variant="outlined"
          sx={{ paddingX: '4rem', height: '100%', borderRadius: 2 }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography
                  variant="body1"
                  color="textSecondary"
                  fontWeight="bold"
                >
                  Percentilplacering
                </Typography>
                <InfoTooltipIcon title="Her vises percentilplaceringen for forbruget." />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Typography variant="h6" fontWeight="bold">
                {formatNumber(benchmarkPoint.cdf * 100)} %
              </Typography>
            </Box>
          </CardContent>
        </Card>
        <Card
          variant="outlined"
          sx={{ paddingX: '4rem', height: '100%', borderRadius: 2 }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography
                  variant="body1"
                  color="textSecondary"
                  fontWeight="bold"
                >
                  Forbrug relativ median
                </Typography>
                <InfoTooltipIcon title="Her vises forbruget relativt til medianen." />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Typography variant="h6" fontWeight="bold">
                {formatNumber(benchmarkPoint.relative)} %
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
      <Box sx={{ width: '50vw', margin: 'auto' }}>
        <HighchartsReact
          highcharts={Highcharts}
          options={options}
          ref={chartComponentRef}
        />
      </Box>
    </>
  );
};

export default PortfolioAnalysisBenchmarkChart;
